import { type Crop } from "react-image-crop";
import logger from "~/utils/logger";

export async function copyImageToClipboard(blob: Blob): Promise<boolean> {
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      return true;
    } catch (error) {
      logger.warn("ClipboardItem write failed", { error });
      return false;
    }
  }
  return false;
}

export async function extractAndCopyCrop(
  img: HTMLImageElement,
  crop: Crop,
  rotation = 0,
  cssScale = 1
): Promise<boolean> {
  if (!crop.width || !crop.height) return false;

  const natW = img.naturalWidth;
  const natH = img.naturalHeight;
  const deg = ((rotation % 360) + 360) % 360;
  const isSwapped = deg === 90 || deg === 270;

  // The rotated image has swapped dimensions when rotated 90/270
  const rotW = isSwapped ? natH : natW;
  const rotH = isSwapped ? natW : natH;

  // First render the full image with rotation applied onto a temp canvas
  const fullCanvas = document.createElement("canvas");
  fullCanvas.width = rotW;
  fullCanvas.height = rotH;

  const fullCtx = fullCanvas.getContext("2d");
  if (!fullCtx) return false;

  fullCtx.save();
  fullCtx.translate(rotW / 2, rotH / 2);
  fullCtx.rotate((deg * Math.PI) / 180);
  fullCtx.drawImage(img, -natW / 2, -natH / 2);
  fullCtx.restore();

  // Compute the visual (post-CSS-transform) dimensions of the image.
  const visualW = (isSwapped ? img.height : img.width) * cssScale;
  const visualH = (isSwapped ? img.width : img.height) * cssScale;

  // Offset of the visual image within the layout container (transform-origin: center)
  const offsetX = (img.width - visualW) / 2;
  const offsetY = (img.height - visualH) / 2;

  // Adjust crop coordinates from container (layout) space to visual image space
  const adjX = crop.x - offsetX;
  const adjY = crop.y - offsetY;

  // Scale from visual image size to rotated-canvas (natural) size
  const scaleX = rotW / visualW;
  const scaleY = rotH / visualH;

  const srcX = Math.max(0, Math.round(adjX * scaleX));
  const srcY = Math.max(0, Math.round(adjY * scaleY));
  const srcW = Math.min(rotW - srcX, Math.round(crop.width * scaleX));
  const srcH = Math.min(rotH - srcY, Math.round(crop.height * scaleY));

  if (srcW <= 0 || srcH <= 0) return false;

  // Extract the cropped region
  const cropCanvas = document.createElement("canvas");
  cropCanvas.width = srcW;
  cropCanvas.height = srcH;

  const cropCtx = cropCanvas.getContext("2d");
  if (!cropCtx) return false;

  cropCtx.drawImage(fullCanvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    cropCanvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Blob failed"))),
      "image/png",
      1.0
    );
  });

  return copyImageToClipboard(blob);
}
